import React from "react";
import { formatNumber, formatMethodName } from "../../utils/formatting";
import type { OpsResponse, BotMethodType } from "../../pages/AdminDashboard";

interface AdminOpsTabProps {
    ops: OpsResponse | null;
    opsRange: "24h" | "7d" | "30d" | "90d";
    setOpsRange: (range: "24h" | "7d" | "30d" | "90d") => void;
    loadingOps: boolean;
    fetchOps: () => void;
}

const AdminOpsTab: React.FC<AdminOpsTabProps> = ({
    ops,
    opsRange,
    setOpsRange,
    loadingOps,
    fetchOps,
}) => {
    return (
        <div className="glass-card" style={{ padding: "16px", borderRadius: "16px", display: "grid", gap: "12px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                    <select
                        value={opsRange}
                        onChange={(e) => setOpsRange(e.target.value as "24h" | "7d" | "30d" | "90d")}
                        className="input-dark"
                        style={{ padding: "8px" }}
                    >
                        <option value="24h">Last 24h</option>
                        <option value="7d">Last 7d</option>
                        <option value="30d">Last 30d</option>
                        <option value="90d">Last 90d</option>
                    </select>
                    <button
                        onClick={() => fetchOps()}
                        style={{
                            padding: "8px 12px",
                            borderRadius: "8px",
                            border: "1px solid rgba(255,255,255,0.15)",
                            background: "rgba(255,255,255,0.06)",
                            color: "white",
                            cursor: "pointer",
                        }}
                    >
                        Refresh
                    </button>
                </div>
                {ops && (
                    <span style={{ color: "#9ca3af", fontSize: "12px" }}>
                        Generated {new Date(ops.generatedAt).toLocaleString()}
                    </span>
                )}
            </div>

            {loadingOps ? (
                <p style={{ color: "#9ca3af" }}>Loading operational metrics...</p>
            ) : ops ? (
                <>
                    <div style={{ display: "grid", gap: "8px" }}>
                        {ops.alerts.map((alert, index) => {
                            const color =
                                alert.severity === "critical" ? "#ef4444" : alert.severity === "warn" ? "#f59e0b" : "#60a5fa";
                            return (
                                <div
                                    key={`${alert.title}:${index}`}
                                    style={{
                                        padding: "10px 12px",
                                        borderRadius: "10px",
                                        border: `1px solid ${color}55`,
                                        background: `${color}22`,
                                    }}
                                >
                                    <div style={{ fontWeight: 700, color }}>{alert.title}</div>
                                    <div style={{ fontSize: "12px", color: "#e5e7eb", marginTop: "3px" }}>{alert.message}</div>
                                </div>
                            );
                        })}
                    </div>

                    <div
                        style={{
                            padding: "10px 12px",
                            borderRadius: "12px",
                            background: "rgba(255,255,255,0.03)",
                            border: "1px solid rgba(255,255,255,0.08)",
                        }}
                    >
                        <div style={{ color: "#9ca3af", fontSize: "12px", marginBottom: "8px" }}>Alert Thresholds (Configured)</div>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                            {[
                                `API warn ${formatNumber(ops.thresholds.apiErrorWarnPct, 1)}%`,
                                `API critical ${formatNumber(ops.thresholds.apiErrorCriticalPct, 1)}%`,
                                `RPC fail warn ${formatNumber(ops.thresholds.rpcFailureWarnPct, 1)}%`,
                                `RPC fail critical ${formatNumber(ops.thresholds.rpcFailureCriticalPct, 1)}%`,
                                `RPC latency warn ${formatNumber(ops.thresholds.rpcHealthWarnLatencyMs, 0)}ms`,
                                `RPC util warn ${formatNumber(ops.thresholds.rpcUtilizationWarnPct, 1)}%`,
                                `RPC util critical ${formatNumber(ops.thresholds.rpcUtilizationCriticalPct, 1)}%`,
                                `Method err warn ${formatNumber(ops.thresholds.methodErrorWarnPct, 1)}%`,
                                `Method err critical ${formatNumber(ops.thresholds.methodErrorCriticalPct, 1)}%`,
                                `Method min runs ${formatNumber(ops.thresholds.methodErrorMinRuns, 0)}`,
                            ].map((label) => (
                                <span
                                    key={label}
                                    style={{
                                        padding: "4px 8px",
                                        borderRadius: "999px",
                                        border: "1px solid rgba(255,255,255,0.12)",
                                        color: "#d1d5db",
                                        fontSize: "11px",
                                    }}
                                >
                                    {label}
                                </span>
                            ))}
                        </div>
                    </div>

                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", gap: "10px" }}>
                        {[
                            { label: "API Requests", value: formatNumber(ops.server.requests.total, 0) },
                            { label: "API Error Rate", value: `${formatNumber(ops.server.requests.errorRate * 100, 2)}%` },
                            { label: "API P95 Latency", value: `${formatNumber(ops.server.requests.p95LatencyMs, 0)}ms` },
                            { label: "RPC Calls", value: formatNumber(ops.rpc.calls.total, 0) },
                            { label: "RPC Failure Rate", value: `${formatNumber(ops.rpc.calls.failureRate * 100, 2)}%` },
                            { label: "RPC P95 Latency", value: `${formatNumber(ops.rpc.calls.p95LatencyMs, 0)}ms` },
                            { label: "Estimated RPC RPM", value: formatNumber(ops.pressure.estimatedRpcRpm, 1) },
                            {
                                label: "RPC Limit Usage",
                                value: ops.pressure.utilizationPct == null ? "N/A" : `${formatNumber(ops.pressure.utilizationPct, 1)}%`,
                            },
                        ].map((item) => (
                            <div
                                key={item.label}
                                style={{
                                    padding: "12px",
                                    borderRadius: "12px",
                                    background: "rgba(255,255,255,0.04)",
                                    border: "1px solid rgba(255,255,255,0.08)",
                                }}
                            >
                                <div style={{ color: "#9ca3af", fontSize: "12px" }}>{item.label}</div>
                                <div style={{ fontSize: "22px", fontWeight: 700, marginTop: "4px" }}>{item.value}</div>
                            </div>
                        ))}
                    </div>

                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "10px" }}>
                        <div
                            style={{
                                padding: "12px",
                                borderRadius: "12px",
                                background: "rgba(255,255,255,0.03)",
                                border: "1px solid rgba(255,255,255,0.08)",
                            }}
                        >
                            <div style={{ color: "#9ca3af", fontSize: "12px", marginBottom: "8px" }}>RPC Health</div>
                            <div style={{ fontWeight: 700, color: ops.rpc.health.ok ? "#14f195" : "#ef4444" }}>
                                {ops.rpc.health.ok ? "Healthy" : "Unhealthy"}
                            </div>
                            <div style={{ color: "#9ca3af", fontSize: "12px", marginTop: "4px" }}>
                                Endpoint: {ops.rpc.endpointHost || "not configured"}
                            </div>
                            <div style={{ color: "#9ca3af", fontSize: "12px", marginTop: "2px" }}>
                                Latency: {ops.rpc.health.latencyMs == null ? "N/A" : `${formatNumber(ops.rpc.health.latencyMs, 0)}ms`}
                            </div>
                            {!ops.rpc.health.ok && ops.rpc.health.error && (
                                <div style={{ color: "#fca5a5", fontSize: "12px", marginTop: "4px" }}>{ops.rpc.health.error}</div>
                            )}
                        </div>
                        <div
                            style={{
                                padding: "12px",
                                borderRadius: "12px",
                                background: "rgba(255,255,255,0.03)",
                                border: "1px solid rgba(255,255,255,0.08)",
                            }}
                        >
                            <div style={{ color: "#9ca3af", fontSize: "12px", marginBottom: "8px" }}>Running Method Pressure</div>
                            {(["volume", "inject", "extract", "defendFloor"] as BotMethodType[]).map((method) => (
                                <div key={method} style={{ display: "flex", justifyContent: "space-between", fontSize: "13px", marginBottom: "4px" }}>
                                    <span style={{ color: "#9ca3af" }}>{formatMethodName(method)}</span>
                                    <span>{ops.pressure.activeMethods[method] || 0}</span>
                                </div>
                            ))}
                            <div style={{ color: "#9ca3af", fontSize: "12px", marginTop: "6px" }}>
                                Running bots: {ops.pressure.runningBots}
                            </div>
                        </div>
                        <div
                            style={{
                                padding: "12px",
                                borderRadius: "12px",
                                background: "rgba(255,255,255,0.03)",
                                border: "1px solid rgba(255,255,255,0.08)",
                            }}
                        >
                            <div style={{ color: "#9ca3af", fontSize: "12px", marginBottom: "8px" }}>Method Run Quality</div>
                            <div style={{ fontSize: "13px", marginBottom: "4px" }}>Total: {ops.quality.methodRuns.total}</div>
                            <div style={{ fontSize: "13px", marginBottom: "4px" }}>Completed: {ops.quality.methodRuns.completed}</div>
                            <div style={{ fontSize: "13px", marginBottom: "4px" }}>Errored: {ops.quality.methodRuns.errored}</div>
                            <div style={{ fontSize: "13px", marginBottom: "4px" }}>Stopped: {ops.quality.methodRuns.stopped}</div>
                            <div style={{ color: "#9ca3af", fontSize: "12px", marginTop: "6px" }}>
                                Error rate: {formatNumber(ops.quality.methodRuns.errorRate * 100, 2)}%
                            </div>
                        </div>
                    </div>

                    <div style={{ overflowX: "auto" }}>
                        <div style={{ minWidth: "760px" }}>
                            <h3 style={{ marginBottom: "8px", fontSize: "15px" }}>Top API Routes</h3>
                            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
                                <thead>
                                    <tr style={{ color: "#9ca3af", textAlign: "left" }}>
                                        <th style={{ padding: "8px" }}>Route</th>
                                        <th style={{ padding: "8px" }}>Count</th>
                                        <th style={{ padding: "8px" }}>Errors</th>
                                        <th style={{ padding: "8px" }}>Error %</th>
                                        <th style={{ padding: "8px" }}>Avg Latency</th>
                                        <th style={{ padding: "8px" }}>P95 Latency</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {ops.server.topRoutes.length === 0 && (
                                        <tr>
                                            <td style={{ padding: "8px", color: "#9ca3af" }} colSpan={6}>
                                                No API samples in current window.
                                            </td>
                                        </tr>
                                    )}
                                    {ops.server.topRoutes.map((row) => (
                                        <tr key={`${row.method}:${row.route}`} style={{ borderTop: "1px solid rgba(255,255,255,0.08)" }}>
                                            <td style={{ padding: "8px" }}>{row.method} {row.route}</td>
                                            <td style={{ padding: "8px" }}>{formatNumber(row.count, 0)}</td>
                                            <td style={{ padding: "8px" }}>{formatNumber(row.errors, 0)}</td>
                                            <td style={{ padding: "8px" }}>{formatNumber(row.errorRate * 100, 2)}%</td>
                                            <td style={{ padding: "8px" }}>{formatNumber(row.avgLatencyMs, 0)}ms</td>
                                            <td style={{ padding: "8px" }}>{formatNumber(row.p95LatencyMs, 0)}ms</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <div style={{ overflowX: "auto" }}>
                        <div style={{ minWidth: "760px" }}>
                            <h3 style={{ marginBottom: "8px", fontSize: "15px" }}>Top RPC Methods</h3>
                            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
                                <thead>
                                    <tr style={{ color: "#9ca3af", textAlign: "left" }}>
                                        <th style={{ padding: "8px" }}>Method</th>
                                        <th style={{ padding: "8px" }}>Count</th>
                                        <th style={{ padding: "8px" }}>Failed</th>
                                        <th style={{ padding: "8px" }}>Failure %</th>
                                        <th style={{ padding: "8px" }}>Avg Latency</th>
                                        <th style={{ padding: "8px" }}>P95 Latency</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {ops.rpc.topMethods.length === 0 && (
                                        <tr>
                                            <td style={{ padding: "8px", color: "#9ca3af" }} colSpan={6}>
                                                No RPC samples in current window.
                                            </td>
                                        </tr>
                                    )}
                                    {ops.rpc.topMethods.map((row) => (
                                        <tr key={row.method} style={{ borderTop: "1px solid rgba(255,255,255,0.08)" }}>
                                            <td style={{ padding: "8px" }}>{row.method}</td>
                                            <td style={{ padding: "8px" }}>{formatNumber(row.count, 0)}</td>
                                            <td style={{ padding: "8px" }}>{formatNumber(row.failed, 0)}</td>
                                            <td style={{ padding: "8px" }}>{formatNumber(row.failureRate * 100, 2)}%</td>
                                            <td style={{ padding: "8px" }}>{formatNumber(row.avgLatencyMs, 0)}ms</td>
                                            <td style={{ padding: "8px" }}>{formatNumber(row.p95LatencyMs, 0)}ms</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </>
            ) : (
                <p style={{ color: "#9ca3af" }}>No ops data loaded yet.</p>
            )}
        </div>
    );
};

export default AdminOpsTab;
